import React, { useState } from "react";
import * as XLSX from "xlsx";

export default function UploadCodeModal({ onClose, onSubmit }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleDownloadSample = () => {
    const sampleData = [
      { code: "ASDFSAas" },
      { code: "asdbsasd" },
      { code: "TEST1234" },
      { code: "ASSESSMENT001" },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const csv = XLSX.utils.sheet_to_csv(worksheet);

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "sample.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];

    setError("");
    setFile(null);

    if (!selectedFile) return;

    const allowedExtensions = [".xlsx", ".csv"];
    const extension = selectedFile.name
      .substring(selectedFile.name.lastIndexOf("."))
      .toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      setError("Please upload only CSV or XLSX files.");
      return;
    }

    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select an Excel or CSV file.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const arrayBuffer = await file.arrayBuffer();

      const workbook = XLSX.read(arrayBuffer, {
        type: "array",
      });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const rawData = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
      });

      if (!rawData.length) {
        setError("The uploaded file is empty.");
        return;
      }

      const hasCodeColumn = Object.keys(rawData[0]).some(
        (key) => key.toLowerCase().trim() === "code"
      );

      if (!hasCodeColumn) {
        setError('File must contain a "code" column.');
        return;
      }

      const formattedData = rawData
        .map((item) => {
          const codeKey = Object.keys(item).find(
            (key) => key.toLowerCase().trim() === "code"
          );

          return {
            code: String(item[codeKey] ?? "").trim(),
          };
        })
        .filter((item) => item.code !== "");

      if (!formattedData.length) {
        setError("No valid assessment codes found in the file.");
        return;
      }

      console.log("Uploaded Codes:", formattedData);
      onSubmit(formattedData);
    } catch (err) {
      console.error(err);
      setError("Unable to read the uploaded file.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-5">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Upload Assessment Codes
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Upload assessment codes using CSV or Excel file
            </p>
          </div>

          {/* Purchase Assessment Code */}
          <button
            type="button"
            onClick={() => console.log("Purchase Assessment Code")}
            className="rounded-lg bg-gradient-to-r from-[#673AB7] to-[#1976D2] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:opacity-90 active:scale-95"
          >
            Purchase Assessment Code
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {/* Upload Area */}
          <label
            htmlFor="assessment-code-file"
            className="group flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-purple-50/20 px-6 py-8 transition hover:border-[#673AB7] hover:bg-purple-50/50"
          >
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 text-[#673AB7] transition group-hover:scale-110">
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>

            <p className="text-sm font-medium text-gray-700">
              Click to upload your file
            </p>

            <p className="mt-1 text-xs text-gray-500">
              CSV or XLSX files only
            </p>

            <input
              id="assessment-code-file"
              type="file"
              accept=".csv,.xlsx"
              className="hidden"
              onChange={handleFileChange}
            />
          </label>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Don't have a template?
            </p>

            <button
              type="button"
              onClick={handleDownloadSample}
              className="flex items-center gap-1.5 text-xs font-semibold bg-gradient-to-r from-[#673AB7] to-[#1976D2] bg-clip-text text-transparent hover:opacity-80"
            >
              <svg
                className="h-4 w-4 text-[#673AB7]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 3v12m0 0l4-4m-4 4l-4-4M5 21h14"
                />
              </svg>
              Download Sample CSV
            </button>
          </div>

          {/* Selected File */}
          {file && (
            <div className="mt-4 flex items-center justify-between rounded-lg border border-purple-100 bg-purple-50/30 p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100">
                  <svg
                    className="h-5 w-5 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>

                <div>
                  <p className="text-xs font-medium text-gray-800">
                    {file.name}
                  </p>
                  <p className="text-[10px] text-gray-500">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-xs font-medium text-red-500 hover:text-red-700"
              >
                Remove
              </button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-600">
              {error}
            </div>
          )}

          {/* Condensed File Format Note */}
          <div className="mt-4 flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2.5 text-xs text-gray-600">
            <span>
              Required column header: <strong className="font-semibold text-gray-800">code</strong>
            </span>
            <span className="rounded bg-purple-100 px-2 py-0.5 text-[10px] font-semibold text-[#673AB7]">
              CSV / XLSX
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={!file || loading}
            onClick={handleUpload}
            className="rounded-lg bg-gradient-to-r from-[#673AB7] to-[#1976D2] px-5 py-2 text-sm font-medium text-white shadow-md transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Uploading..." : "Upload Codes"}
          </button>
        </div>
      </div>
    </div>
  );
}